import React, { useState, useEffect, useRef, Suspense } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { s } from "./style";
import RouteFallback from "@app/layout/route-fallback";
import RouteErrorBoundary from "@app/layout/route-error-boundary";
import mainLogoImage from "@shared/assets/images/main_logo.png";
import menuIcon from "@shared/assets/icons/drawer-menu/menu.svg";
import userIcon from "@shared/assets/icons/drawer-menu/account_circle.svg";
import lossAssetIcon from "@shared/assets/icons/drawer-menu/lossAssetIcon.svg";
import electricBikeIcon from "@shared/assets/icons/drawer-menu/electric_bike.svg";
import usageHistoryIcon from "@shared/assets/icons/drawer-menu/play_list_add_check.svg";
import financialIcon from "@shared/assets/icons/drawer-menu/financial.svg";
import logOutIcon from "@shared/assets/icons/drawer-menu/log_out.svg";
import menuOpenIcon from "@shared/assets/icons/drawer-menu/menu_open.svg";
import menuCloseIcon from "@shared/assets/icons/drawer-menu/menu_close.svg";
import SizedBox from "@shared/ui/sized-box";
import { useResizeAutoOpen } from "./model/useResizeAutoOpen";
import { useClickOutsideClose } from "./model/useClickOutsideClose";
import { useLogout } from "@app/layout/drawer-layout/model/useLogout";

function DrawerLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // 서브메뉴 상태
    const [isElectricBikeSubMenuOpen, setIsElectricBikeSubMenuOpen] = useState(false);
    const [isProblemSubMenuOpen, setIsProblemSubMenuOpen] = useState(false);
    const [isUsageHistorySubMenuOpen, setIsUsageHistorySubMenuOpen] = useState(false);
    const [isFinancialsSubMenuOpen, setIsFinancialsSubMenuOpen] = useState(false);

    const [goTo, setGoTo] = useState<string | null>(null);
    const drawerRef = useRef<HTMLDivElement>(null);
    const { handleLogoutClick } = useLogout();

    useResizeAutoOpen(setIsDrawerOpen);
    useClickOutsideClose(isDrawerOpen, drawerRef, setIsDrawerOpen);

    useEffect(() => {
        if (goTo) {
            navigate(goTo);
            setGoTo(null);
        }
    }, [goTo, navigate]);

    // 단일 메뉴 클릭 시 다른 서브메뉴는 닫기
    const handleMenuClick = () => {
        setIsElectricBikeSubMenuOpen(false);
        setIsProblemSubMenuOpen(false);
        setIsUsageHistorySubMenuOpen(false);
        setIsFinancialsSubMenuOpen(false);
    };

    const toggleDrawer = () => setIsDrawerOpen((p) => !p);

    // 기기 관리 토글
    const toggleElectricBikeSubMenu = () => {
        setIsElectricBikeSubMenuOpen((p) => !p);
        setGoTo("/home/bike-overview");
        setIsProblemSubMenuOpen(false);
        setIsUsageHistorySubMenuOpen(false);
        setIsFinancialsSubMenuOpen(false);
    };

    // 문제 관리 토글
    const toggleProblemSubMenu = () => {
        setIsProblemSubMenuOpen((p) => !p);
        setGoTo("/home/bike-asset"); // 기본 이동(자전거 관리)
        setIsElectricBikeSubMenuOpen(false);
        setIsUsageHistorySubMenuOpen(false);
        setIsFinancialsSubMenuOpen(false);
    };

    // 이용 내역 관리 토글
    const toggleUsageHistorySubMenu = () => {
        setIsUsageHistorySubMenuOpen((p) => !p);
        setGoTo("/home/usage-histories");
        setIsElectricBikeSubMenuOpen(false);
        setIsProblemSubMenuOpen(false);
        setIsFinancialsSubMenuOpen(false);
    };

    // 재정 관리 토글
    const toggleFinancialsSubMenu = () => {
        setIsFinancialsSubMenuOpen((p) => !p);
        setGoTo("/home/revenue");
        setIsElectricBikeSubMenuOpen(false);
        setIsProblemSubMenuOpen(false);
        setIsUsageHistorySubMenuOpen(false);
    };

    return (
        <div style={{ display: "flex", position: "relative" }}>
            <s.MenuButton onClick={toggleDrawer}>
                <img src={menuIcon} alt="Menu" draggable="false" />
            </s.MenuButton>

            <s.DrawerContainer ref={drawerRef} $isOpen={isDrawerOpen}>
                <SizedBox height={56} />
                <img src={mainLogoImage} alt="logo" draggable="false" />
                <SizedBox height={56} />

                <s.DrawerMenu>
                    {/* 1. 대시보드 */}
                    {/*<s.MenuItem*/}
                    {/*    to="/home/dashboard"*/}
                    {/*    className={({ isActive }) => (isActive ? "active" : "")}*/}
                    {/*    onClick={handleMenuClick}*/}
                    {/*>*/}
                    {/*    <s.MenuItemWrapper>*/}
                    {/*        <s.MenuItemImage src={dashboardIcon} draggable="false" />*/}
                    {/*        대시보드*/}
                    {/*    </s.MenuItemWrapper>*/}
                    {/*</s.MenuItem>*/}

                    {/* 2. 회원관리 */}
                    <s.MenuItem
                        to="/home/user"
                        className={({ isActive }) => (isActive ? "active" : "")}
                        onClick={handleMenuClick}
                    >
                        <s.MenuItemWrapper>
                            <s.MenuItemImage src={userIcon} draggable="false" />
                            회원 관리
                        </s.MenuItemWrapper>
                    </s.MenuItem>

                    {/* 3. 기기관리 */}
                    <s.MenuButtonItem onClick={toggleElectricBikeSubMenu}>
                        <s.MenuItemWrapper>
                            <s.MenuItemImage src={electricBikeIcon} draggable="false" />
                            기기 관리
                        </s.MenuItemWrapper>
                        <img
                            src={isElectricBikeSubMenuOpen ? menuCloseIcon : menuOpenIcon}
                            alt="menu toggle"
                            draggable="false"
                        />
                    </s.MenuButtonItem>
                    {isElectricBikeSubMenuOpen && (
                        <s.SubMenu>
                            <s.SubMenuItem to="/home/bike-overview" className={({ isActive }) => (isActive ? "active" : "")}>
                                자전거 현황
                            </s.SubMenuItem>
                            <s.SubMenuItem to="/home/hub-overview" className={({ isActive }) => (isActive ? "active" : "")}>
                                허브 현황
                            </s.SubMenuItem>
                            <s.SubMenuItem to="/home/now-use-bikes" className={({ isActive }) => (isActive ? "active" : "")}>
                                이용 현황
                            </s.SubMenuItem>
                            <s.SubMenuItem to="/home/hub-map" className={({ isActive }) => (isActive ? "active" : "")}>
                                허브 지도
                            </s.SubMenuItem>
                        </s.SubMenu>
                    )}

                    {/* 4. 문제관리 */}
                    <s.MenuButtonItem onClick={toggleProblemSubMenu}>
                        <s.MenuItemWrapper>
                            <s.MenuItemImage src={lossAssetIcon} draggable="false" />
                            문제 관리
                        </s.MenuItemWrapper>
                        <img
                            src={isProblemSubMenuOpen ? menuCloseIcon : menuOpenIcon}
                            alt="menu toggle"
                            draggable="false"
                        />
                    </s.MenuButtonItem>
                    {isProblemSubMenuOpen && (
                        <s.SubMenu>
                            <s.SubMenuItem to="/home/bike-asset" className={({ isActive }) => (isActive ? "active" : "")}>
                                자전거 관리
                            </s.SubMenuItem>
                            <s.SubMenuItem to="/home/station-asset" className={({ isActive }) => (isActive ? "active" : "")}>
                                스테이션 관리
                            </s.SubMenuItem>
                            <s.SubMenuItem to="/home/defective-bikes" className={({ isActive }) => (isActive ? "active" : "")}>
                                이상 기기
                            </s.SubMenuItem>
                        </s.SubMenu>
                    )}

                    {/* 5. 이용 내역 관리 */}
                    <s.MenuButtonItem onClick={toggleUsageHistorySubMenu}>
                        <s.MenuItemWrapper>
                            <s.MenuItemImage src={usageHistoryIcon} draggable="false" />
                            이용 내역 관리
                        </s.MenuItemWrapper>
                        <img
                            src={isUsageHistorySubMenuOpen ? menuCloseIcon : menuOpenIcon}
                            alt="menu toggle"
                            draggable="false"
                        />
                    </s.MenuButtonItem>
                    {isUsageHistorySubMenuOpen && (
                        <s.SubMenu>
                            <s.SubMenuItem to="/home/usage-histories" className={({ isActive }) => (isActive ? "active" : "")}>
                                이용 내역
                            </s.SubMenuItem>
                            <s.SubMenuItem to="/home/usage-analytics" className={({ isActive }) => (isActive ? "active" : "")}>
                                이용 분석
                            </s.SubMenuItem>
                        </s.SubMenu>
                    )}

                    {/* 6. 재정관리 */}
                    <s.MenuButtonItem onClick={toggleFinancialsSubMenu}>
                        <s.MenuItemWrapper>
                            <s.MenuItemImage src={financialIcon} draggable="false" />
                            재정 관리
                        </s.MenuItemWrapper>
                        <img
                            src={isFinancialsSubMenuOpen ? menuCloseIcon : menuOpenIcon}
                            draggable="false"
                            alt="menu toggle"
                        />
                    </s.MenuButtonItem>
                    {isFinancialsSubMenuOpen && (
                        <s.SubMenu>
                            <s.SubMenuItem to="/home/revenue" className={({ isActive }) => (isActive ? "active" : "")}>
                                매출
                            </s.SubMenuItem>
                        </s.SubMenu>
                    )}

                    {/* 임시 지도 */}
                    {/*<s.MenuItem*/}
                    {/*    to="/home/upload-map"*/}
                    {/*    className={({ isActive }) => (isActive ? "active" : "")}*/}
                    {/*    onClick={handleMenuClick}*/}
                    {/*>*/}
                    {/*    <s.MenuItemWrapper>*/}
                    {/*        <s.MenuItemImage src={dashboardIcon} draggable="false" />*/}
                    {/*        지도 데이터 확인*/}
                    {/*    </s.MenuItemWrapper>*/}
                    {/*</s.MenuItem>*/}
                    
                    {/* 7. 로그아웃 */}
                    <s.MenuButtonItem onClick={handleLogoutClick}>
                        <s.MenuItemWrapper>
                            <s.MenuItemImage src={logOutIcon} draggable="false" />
                            로그아웃
                        </s.MenuItemWrapper>
                    </s.MenuButtonItem>
                </s.DrawerMenu>
            </s.DrawerContainer>

            <s.ContentContainer>
                {/* key={pathname}: 라우트 변경 시 경계를 리셋 → 일시적 청크 로드 실패가 다음 이동에서 복구된다 */}
                <RouteErrorBoundary key={location.pathname}>
                    <Suspense fallback={<RouteFallback />}>
                        <Outlet />
                    </Suspense>
                </RouteErrorBoundary>
            </s.ContentContainer>
        </div>
    );
}

export default DrawerLayout;
